import { toCents } from "../../utils/money";
import { margenDelArqueo } from "../../services/margenDelArqueo";

/**
 * Quién obliga a pedir la contraseña del supervisor para cerrar la caja.
 *
 * La regla vivía suelta dentro del formulario, en medio de mil cien líneas, y
 * **no tenía ni una prueba**: la única que existía vigilaba que la comprobación
 * de credenciales se esperara con `await` --el defecto que dejaba entrar a
 * cualquiera-- pero nadie comprobaba *cuándo* se pide.
 *
 * Y ahí se escondía el hueco que apareció al cerrar una caja abierta en ceros:
 * **si la tabla del cuadre no tiene filas, no hay nada que comparar y el cierre
 * pasaba de largo**. Con ventas siempre hay filas, así que en la operación
 * normal no se notaba; sin ventas, las filas salen sólo del catálogo de formas
 * de pago, y si ese catálogo todavía no cargó --o no se ha sincronizado nunca--
 * la tabla queda vacía y el arqueo se cierra sin que nadie lo mire.
 *
 * Un cajón con dinero dentro y una caja abierta en ceros es exactamente el caso
 * en que más falta hace preguntar.
 */

/**
 * Las formas de pago cuya diferencia supera lo admitido.
 *
 * Se valida cada forma por separado --y no sólo el total-- para que una
 * diferencia no quede tapada por otra de signo contrario: falta efectivo y sobra
 * tarjeta suman cero y son dos problemas, no ninguno.
 *
 * @param {Array<{nombre?: string, codigoMetodoPago?: string, digitado?: number, cobro?: number}>} filas
 * @param {{margenCierre?: number|string}} [tpv] Ficha del TPV, para su margen.
 * @param {number} decimales Precisión de cálculo del país.
 */
export const metodosConDiferencia = (filas, tpv, decimales) => {
  const umbralCents = toCents(margenDelArqueo(tpv), decimales);

  return (filas ?? [])
    .map((fila) => ({
      nombre: fila.nombre || fila.codigoMetodoPago,
      diferenciaCents: toCents(fila.digitado ?? 0, decimales) - toCents(fila.cobro ?? 0, decimales),
    }))
    .filter((fila) => Math.abs(fila.diferenciaCents) > umbralCents);
};

/**
 * Si hay que pedir autorización antes de cerrar.
 *
 * Tres motivos, ordenados de más a menos específico, porque el que se devuelve
 * es el que se le explica al cajero y conviene que sea el más informativo:
 *
 * 1. **Alguna forma de pago se sale del margen.** Se pueden nombrar.
 * 2. **No hay nada que cuadrar y aun así se contó dinero.** Sin filas no se
 *    puede comparar nada, así que cerrar sería afirmar que cuadra sin haberlo
 *    comprobado. Es el hueco que apareció al cerrar una caja abierta en ceros.
 * 3. **El turno pasó sin un solo movimiento.** Requisito del cliente: una caja
 *    que se abre y se cierra sin que ocurra nada no la cierra un cajero solo,
 *    aunque el arqueo cuadre. Cuadrar en cero es fácil, y precisamente por eso
 *    conviene que alguien más lo mire.
 *
 *    **Que el neto dé cero no basta**: una factura y su nota de crédito por el
 *    mismo importe también suman cero, y eso es operación normal --el turno
 *    trabajó y la venta se devolvió--. Lo que pide autorización es la ausencia
 *    de movimiento, no el resultado en cero. Distinguirlos es justamente lo que
 *    hace `motivoDelCierreEnCero`.
 *
 * @param {boolean} turnoSinMovimiento  Si el turno no tuvo ni una línea de cobro.
 * @returns {{hayQuePedirla: boolean, motivo: "diferencia"|"sinCuadre"|"sinMovimiento"|null, metodos: Array}}
 */
export const autorizacionDelArqueo = (
  filas,
  tpv,
  decimales,
  arqueoDigitado = 0,
  turnoSinMovimiento = false
) => {
  const metodos = metodosConDiferencia(filas, tpv, decimales);
  if (metodos.length > 0) return { hayQuePedirla: true, motivo: "diferencia", metodos };

  const sinFilas = !filas || filas.length === 0;
  if (sinFilas && toCents(arqueoDigitado ?? 0, decimales) !== 0) {
    return { hayQuePedirla: true, motivo: "sinCuadre", metodos: [] };
  }

  if (turnoSinMovimiento) return { hayQuePedirla: true, motivo: "sinMovimiento", metodos: [] };

  return { hayQuePedirla: false, motivo: null, metodos: [] };
};
